import { useState, useCallback, useMemo, useRef } from 'react';
import {
  getDaysInMonth,
  startOfMonth,
  getDay,
  format,
  isToday as isTodayFn,
  addMonths,
  subMonths,
  setMonth,
  setYear,
} from 'date-fns';
import { CalendarAccent } from '../ui/CalendarAccent';

export interface CalendarViewProps {
  /** Array of date strings (YYYY-MM-DD) that have existing entries */
  entryDates: string[];
  /** Callback when user selects a date. Receives ISO date string (YYYY-MM-DD). */
  onDateSelect: (date: string) => void;
}

type ViewMode = 'calendar' | 'year-select';
type SlideDirection = 'left' | 'right' | 'none';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

/**
 * CalendarView displays a navigable month grid for the Journal App.
 * Features:
 * - Month grid with day cells showing the current month
 * - Current date highlighting
 * - Dot indicators for dates with existing entries
 * - Month navigation with 300ms ease-in-out transition
 * - Year selector showing 12-month grid (2000–2100 range)
 * - Accessible date selection with proper button roles and aria-labels
 */
export function CalendarView({ entryDates, onDateSelect }: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('none');
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const gridRef = useRef<HTMLDivElement>(null);

  // Convert entryDates strings to a Set for O(1) lookup
  const entryDateSet = useMemo(() => new Set(entryDates), [entryDates]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfMonth = getDay(startOfMonth(currentDate));

  // Generate day cells for the current month
  const dayCells = useMemo(() => {
    const cells: (number | null)[] = [];
    // Fill leading empty cells
    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(null);
    }
    // Fill day numbers
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(day);
    }
    return cells;
  }, [firstDayOfMonth, daysInMonth]);

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    if (isAnimating) return;

    setSlideDirection(direction === 'next' ? 'left' : 'right');
    setIsAnimating(true);

    // After animation completes, update the month
    setTimeout(() => {
      setCurrentDate(prev =>
        direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1)
      );
      setSlideDirection('none');
      setIsAnimating(false);
    }, 300);
  }, [isAnimating]);

  const handlePrevMonth = useCallback(() => navigateMonth('prev'), [navigateMonth]);
  const handleNextMonth = useCallback(() => navigateMonth('next'), [navigateMonth]);

  const handleDateClick = useCallback((day: number) => {
    const selectedDate = new Date(year, month, day);
    const dateString = format(selectedDate, 'yyyy-MM-dd');
    onDateSelect(dateString);
  }, [year, month, onDateSelect]);

  const handleHeaderClick = useCallback(() => {
    setSelectedYear(year);
    setViewMode('year-select');
  }, [year]);

  const handleMonthSelect = useCallback((monthIndex: number) => {
    setCurrentDate(setMonth(setYear(new Date(), selectedYear), monthIndex));
    setViewMode('calendar');
  }, [selectedYear]);

  const handleYearPrev = useCallback(() => {
    setSelectedYear(prev => Math.max(MIN_YEAR, prev - 1));
  }, []);

  const handleYearNext = useCallback(() => {
    setSelectedYear(prev => Math.min(MAX_YEAR, prev + 1));
  }, []);

  const hasEntry = useCallback((day: number): boolean => {
    const dateString = format(new Date(year, month, day), 'yyyy-MM-dd');
    return entryDateSet.has(dateString);
  }, [year, month, entryDateSet]);

  const isDayToday = useCallback((day: number): boolean => {
    return isTodayFn(new Date(year, month, day));
  }, [year, month]);

  // Determine animation transform class
  const getAnimationStyle = (): React.CSSProperties => {
    if (slideDirection === 'left') {
      return { transform: 'translateX(-100%)', opacity: 0 };
    }
    if (slideDirection === 'right') {
      return { transform: 'translateX(100%)', opacity: 0 };
    }
    return { transform: 'translateX(0)', opacity: 1 };
  };

  if (viewMode === 'year-select') {
    return (
      <div className="relative p-4 bg-bg-card rounded-md shadow-card" role="region" aria-label="Year and month selector">
        {/* Decorative accents */}
        <div className="absolute top-2 right-2 opacity-50">
          <CalendarAccent variant="sparkle" size={14} />
        </div>
        <div className="absolute bottom-2 left-2 opacity-40">
          <CalendarAccent variant="heart" size={12} />
        </div>

        {/* Year navigation header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleYearPrev}
            disabled={selectedYear <= MIN_YEAR}
            className="p-2 rounded-sm text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-all duration-fast disabled:opacity-50 disabled:pointer-events-none"
            aria-label="Previous year"
          >
            <ChevronLeft />
          </button>
          <span className="text-heading-3 font-semibold text-text-heading">
            {selectedYear}
          </span>
          <button
            onClick={handleYearNext}
            disabled={selectedYear >= MAX_YEAR}
            className="p-2 rounded-sm text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-all duration-fast disabled:opacity-50 disabled:pointer-events-none"
            aria-label="Next year"
          >
            <ChevronRight />
          </button>
        </div>

        {/* 12-month grid */}
        <div className="grid grid-cols-3 gap-2" role="grid" aria-label={`Months of ${selectedYear}`}>
          {MONTHS.map((monthName, index) => {
            const isCurrentMonth = selectedYear === new Date().getFullYear() && index === new Date().getMonth();
            return (
              <button
                key={monthName}
                onClick={() => handleMonthSelect(index)}
                className={[
                  'py-3 px-2 rounded-sm text-sm font-medium transition-all duration-fast',
                  'hover:bg-primary-light hover:text-text-primary',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                  isCurrentMonth
                    ? 'bg-primary text-white shadow-card'
                    : 'text-text-secondary hover:bg-hover-bg',
                ].join(' ')}
                aria-label={`${monthName} ${selectedYear}`}
              >
                {monthName.slice(0, 3)}
              </button>
            );
          })}
        </div>

        {/* Back to calendar button */}
        <button
          onClick={() => setViewMode('calendar')}
          className="mt-4 w-full py-2 text-sm text-text-secondary hover:text-text-primary transition-all duration-fast rounded-sm hover:bg-hover-bg"
          aria-label="Back to calendar view"
        >
          Back to Calendar
        </button>
      </div>
    );
  }

  return (
    <div className="relative p-4 bg-bg-card rounded-md shadow-card" role="region" aria-label="Calendar">
      {/* Decorative accents */}
      <div className="absolute top-2 right-2 opacity-50 pointer-events-none">
        <CalendarAccent variant="flower" size={16} />
      </div>
      <div className="absolute top-2 left-2 opacity-40 pointer-events-none">
        <CalendarAccent variant="star" size={12} />
      </div>
      <div className="absolute bottom-2 right-2 opacity-30 pointer-events-none">
        <CalendarAccent variant="heart" size={14} />
      </div>

      {/* Month/Year header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrevMonth}
          disabled={isAnimating}
          className="p-2 rounded-sm text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-all duration-fast disabled:opacity-50"
          aria-label="Previous month"
        >
          <ChevronLeft />
        </button>

        <button
          onClick={handleHeaderClick}
          className="text-heading-3 font-semibold text-text-heading hover:text-primary transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm px-2 py-1"
          aria-label={`${format(currentDate, 'MMMM yyyy')}. Click to select year and month`}
        >
          {format(currentDate, 'MMMM yyyy')}
        </button>

        <button
          onClick={handleNextMonth}
          disabled={isAnimating}
          className="p-2 rounded-sm text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-all duration-fast disabled:opacity-50"
          aria-label="Next month"
        >
          <ChevronRight />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-2" role="row">
        {WEEKDAYS.map(day => (
          <div
            key={day}
            className="text-center text-sm text-text-secondary font-medium py-1"
            role="columnheader"
            aria-label={day}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Day grid with transition animation */}
      <div
        ref={gridRef}
        className="calendar-transition"
        style={getAnimationStyle()}
        aria-live="polite"
      >
        <div className="grid grid-cols-7 gap-1" role="grid" aria-label={`${format(currentDate, 'MMMM yyyy')} days`}>
          {dayCells.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="aspect-square" role="gridcell" />;
            }

            const today = isDayToday(day);
            const hasEntryForDay = hasEntry(day);
            const dateLabel = format(new Date(year, month, day), 'EEEE, MMMM d, yyyy');

            return (
              <button
                key={`day-${day}`}
                onClick={() => handleDateClick(day)}
                className={[
                  'relative flex flex-col items-center justify-center aspect-square rounded-sm',
                  'text-sm font-medium transition-all duration-fast',
                  'hover:bg-primary-light hover:shadow-card',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                  today
                    ? 'bg-primary text-white shadow-card ring-2 ring-primary-dark'
                    : 'text-text-primary hover:text-text-heading',
                ].join(' ')}
                role="gridcell"
                aria-label={`${dateLabel}${hasEntryForDay ? ', has entry' : ''}`}
                aria-current={today ? 'date' : undefined}
              >
                <span>{day}</span>
                {/* Dot indicator for dates with entries */}
                {hasEntryForDay && (
                  <span
                    className={[
                      'absolute bottom-1 w-1.5 h-1.5 rounded-full',
                      today ? 'bg-white' : 'bg-primary',
                    ].join(' ')}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Simple chevron-left icon */
function ChevronLeft() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/** Simple chevron-right icon */
function ChevronRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
